import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react'
import Card from 'antd/lib/card/Card'
import { CommonFilters, UserAvatar, TableDownload } from '@components'
import { getMeetingCallRecordList, getMeetingSharingUrl, exportMeetingCallRecordList, exportMeetingCallRecordWithQCResultList } from '@services/customer'
import { Table, TableProps, message, Input, Button, Select, Typography } from 'antd'
import { MeetingCallRecordFilter, needTotalEnum, sortTypeEnum, MeetingCallRecordListItem, KeyWordTypeEnum } from '@types'
import { useTableService } from '@hooks/useTableService'
import { useTableColumn, TableColumnProps } from '@hooks'
import { getSortOrder, getTableXwidth, TableIdEnum } from '@configs/table'
import { FieldItem, KeyWordSelect } from 'src/components/CommonFilters/configs'
import { formatTime, trimStr, getStaffName, parseDuration, sStorage, storeQueue } from '@library'
import { antdSortMap, MeetingCallEndReasonMap } from '@configs/map'
import { clone, get, isEmpty, cloneDeep } from 'lodash'
import { Link, useHistory } from 'react-router-dom'
import { ConfigTableHeader } from 'src/components/ConfigTableHeader'
import { getGroupsText } from '../utils'
import { getUserAuthPoint } from '@services/session'
import AUTH_POINT_MAP from '@configs/auth-point'
import store, { globalStore } from '@store'
import { FormValues } from '@tencent/meta-form'
import moment from 'moment'
import { handleKeyWordParams } from './TCCCCallRecord'
import { PAGE_TYPE_ENUM } from '../../score-detail/constants/index'

/** 批量评分队列分类 */
const MEETING_SCORE_CATEGORY = 'meeting-score'

/** 批量评分队列数据结构 */
interface MeetingScoreQueueData {
  recordIds: number[]
  pageType: string
}

const initFilter: MeetingCallRecordFilter = {
  page: 1,
  pageSize: 10,
  needTotal: needTotalEnum.ALL,
  sort: 'createTime',
  sortType: sortTypeEnum.DESC,
}
// 通话时长为-1时表示话单还在生成中
const GENERATING_CALLDATA = -1
// 权限点
const {
  menuTaskCallScoreR,
  menuTaskCallScoreU,
  menuTaskCallScoreEx,
} = AUTH_POINT_MAP
// 质检状态-已打分
const SCORED = 1
// 质检状态
const QualityCheckStatusOptions = [
  { label: '已打分', value: String(SCORED) },
  { label: '未打分', value: '0' },
]
// 关键词匹配状态
const MatchKeywordStatusOptions = [
  { label: '未匹配', value: '0' },
  { label: '匹配中', value: '1' },
  { label: '已命中', value: '2' },
  { label: '未命中', value: '3' },
]
// 只有质检管理员才能看到这部分筛选项
const ScoreAdminFilterAvailable = ['matchKeywordStatus', 'salesKeywordList', 'customerKeywordList']
// 只有质检管理员才能看到的列表
const ScoreAdminColumnsAvailable = [{
  title: '命中关键词',
  dataIndex: 'keyWord',
  width: 280,
  render: (val, record) => {
    const { salesKeywordList = [], customerKeywordList = [] } = record
    return (
      <>
        {!isEmpty(salesKeywordList) && (
          <>
            <Typography.Text
              style={{ width: 280 }}
              ellipsis={{ tooltip: salesKeywordList.join('、') }}
            >
              客服关键词：{salesKeywordList.join('、')}；
            </Typography.Text>
            <br />
          </>
        )}
        {!isEmpty(customerKeywordList) && (
          <Typography.Text
            style={{ width: 280 }}
            ellipsis={{ tooltip: customerKeywordList.join('、') }}
          >
            客户关键词：{customerKeywordList.join('、')}；
          </Typography.Text>
        )}
      </>
    )
  },
}]

const meetingCallRecordFilterItems: FieldItem[] = [
  { fieldName: 'meetingCode', label: '关联会议号', type: 'multiTags' },
  { fieldName: 'callNumber', label: '主叫电话', type: 'input' },
  {
    fieldName: 'phone',
    label: '被叫电话',
    render: ({ value, onChange }) => <Input addonBefore="+86" value={value} onChange={e => onChange(e.target.value || undefined)} allowClear />,
    handleValue: val => ({ phone: val || undefined }),
  },
  { fieldName: 'salesName', label: '座席姓名', type: 'staffSelect' },
  { fieldName: 'groupId', label: '服务组', type: 'groupId' },
  {
    fieldName: 'qualityCheckStatus',
    label: '质检状态',
    render: () => <Select placeholder="未筛选则默认全部" allowClear options={QualityCheckStatusOptions} />,
  },
  {
    fieldName: 'matchKeywordStatus',
    label: '关键词匹配状态',
    render: () => <Select showArrow placeholder="未筛选则默认全部" allowClear options={MatchKeywordStatusOptions} />,
  },
  {
    fieldName: 'salesKeywordList',
    label: '客服关键词',
    render: ({ value, onChange }) => (<KeyWordSelect value={value} onChange={onChange} type={KeyWordTypeEnum.customerService} />),
  },
  {
    fieldName: 'customerKeywordList',
    label: '客户关键词',
    render: ({ value, onChange }) => (<KeyWordSelect value={value} onChange={onChange} type={KeyWordTypeEnum.client} />),
  },
  { fieldName: 'qualityCheckSales', label: '质检人', type: 'staffSelect' },
  { fieldName: 'qualityCheckSalesGroupId', label: '质检人所属组', type: 'qualityCheckSalesGroupId' },
  {
    fieldName: 'createTime',
    label: '记录生成时间',
    type: 'rangePicker',
    handleValue: val => ({
      createTimeFrom: formatTime(val?.[0], 'start'),
      createTimeTo: formatTime(val?.[1], 'end'),
    }),
  },
  { fieldName: 'cid', label: 'cid', type: 'multiTags' },
  {
    fieldName: 'duration',
    label: '通话时长(秒)',
    type: 'inputNumberRange',
    handleValue: val => ({
      durationFrom: val?.[0],
      durationTo: val?.[1],
    }),
  },
  { fieldName: 'companyId', label: '所属公司', type: 'company' },
]

const handleMeetingCallRecord = (list: any[]) => list?.map(item => {
  const callRecordData: any = clone(item)
  const isGenerating = item?.duration === GENERATING_CALLDATA

  // 通话时间为-1时，通话时长 展示为空
  callRecordData.durationText = isGenerating ? '' : parseDuration(item?.duration, 's')
  callRecordData.failReasonText = MeetingCallEndReasonMap[item?.failReason] || ''
  callRecordData.groupsText = getGroupsText(item.groupNames)
  return callRecordData
})

// const download = async (record: MeetingCallRecordListItem) => {
//   const [url, err] = await getMeetingPstnDownloadUrl({ recordId: record.recordId, meetingId: record.meetingId })
//   if (err) {
//     message.error(`下载失败: ${err.message}`)
//   } else {
//     const tagName = `${getStaffName(record.salesName, record.salesNameCh)}${trimStr(name, '-')}`
//     downloadByATag(`${tagName}.xlsx`, url.downloadURL)
//   }
// }
export default function MeetingCallRecord () {
  const {
    loading,
    list,
    pagination,
    filter,
    setFilter,
    resetFilter,
    rowKey,
    reload,
    rowSelection,
    indexList,
  } = useTableService(getMeetingCallRecordList, initFilter, '获取会议记录失败')
  const history = useHistory()
  const authPointMap = globalStore(s => s.authPointMap)
  const [authPoint, setAuthPoint] = useState(null)
  const filterRef = useRef(null)
  const handleJumpToMeetingDetail = useCallback((record: MeetingCallRecordListItem) => async () => {
    const [res, err] = await getMeetingSharingUrl({ recordId: record.recordId, meetingId: record.meetingId })
    if (err) {
      message.error(`跳转会议详情失败:(${err.message})`)
      return
    }
    window.open(res.sharingURL, '_blank')
  }, [])
  const meetingCallRecordList = useMemo<MeetingCallRecordListItem[]>(() => handleMeetingCallRecord(list), [list])

  // 通过 indexList 获取选中的行数据
  const selectedRows = useMemo(() => {
    return indexList.map(index => meetingCallRecordList[index]).filter(Boolean)
  }, [meetingCallRecordList, indexList])
  const columns: TableColumnProps<MeetingCallRecordListItem>[] = useMemo(() => [
    {
      title: '记录生成时间',
      dataIndex: 'createTime',
      width: 180,
      fixed: true,
      sorter: true,
      sortOrder: filter.sort === 'createTime' ? antdSortMap[filter.sortType] : undefined,
    },
    {
      title: '客户名称',
      dataIndex: 'customerName',
      width: 280,
      render: (name, record) => (
        <Link target="_blank" to={`/customer/${record.customerId}?from=${encodeURIComponent(window.location.pathname)}`}>
          {trimStr(name, '-')}
        </Link>),
    },
    { title: '所属组', dataIndex: 'groupsText', width: 150 },
    { title: '所属公司', dataIndex: 'companyName', width: 180 },
    { title: '主叫号码', dataIndex: 'callNumber', width: 180 },
    { title: '被叫号码', dataIndex: 'phone', width: 180 },
    { title: '通话结果', dataIndex: 'failReasonText', width: 180 },
    // 只有质检管理员才有权限看到
    ...(authPointMap.menuTaskCallScoreExt1.result ? ScoreAdminColumnsAvailable : []),
    {
      title: '质检人',
      dataIndex: 'qualityCheckSales',
      width: 260,
      render: value => <UserAvatar salesName={value} />,
    },
    { title: '质检时间', dataIndex: 'qualityCheckTime', width: 180 },
    {
      title: '分数',
      dataIndex: 'qualityCheckScore',
      width: 120,
      sorter: true,
      sortOrder: filter.sort === 'qualityCheckScore' ? antdSortMap[filter.sortType] : undefined,
    },
    {
      title: '通话开始时间',
      dataIndex: 'callBeginTime',
      width: 180,
      sorter: true,
      sortOrder: filter.sort === 'callBeginTime' ? antdSortMap[filter.sortType] : undefined,
    },
    {
      title: '通话结束时间',
      dataIndex: 'callEndTime',
      width: 180,
      sorter: true,
      sortOrder: filter.sort === 'callEndTime' ? antdSortMap[filter.sortType] : undefined,
    },
    {
      title: '通话时长',
      dataIndex: 'durationText',
      width: 120,
    },
    { title: '跟进人', dataIndex: 'salesName', width: 240, render: value => <UserAvatar salesName={value} /> },
    {
      title: '关联会议名',
      dataIndex: 'meetingTitle',
      width: 280,
      render: (name, record) => (
        <a onClick={handleJumpToMeetingDetail(record)}>
          {getStaffName(record.salesName, record.salesNameCh)}{trimStr(name, '-')}
        </a>),
    },
    { title: '关联会议号', dataIndex: 'meetingCode', width: 120 },
    {
      title: '操作',
      dataIndex: 'actions',
      width: 100,
      render: (formItem, record, index) => {
        return (
          <>
            {authPoint?.[menuTaskCallScoreU] && (
              <Button
                type="link"
                disabled={!record?.duration}
                style={{ padding: 0 }}
                onClick={() => handleToScoreDetail(record, index)}
              >
                评分
              </Button>
            )}
            {(!authPoint?.[menuTaskCallScoreU] && authPoint?.[menuTaskCallScoreR] && record?.qualityCheckStatus === SCORED) && (
              <Button type="link" style={{ padding: 0 }} onClick={() => handleToScoreDetail(record, index)}>查看评分</Button>
            )}
            {/* <Tooltip title={isToday(record.createTime) ? '通话详单生成中' : ''}> */}
            {/*   <Button disabled={isToday(record.createTime)} type="link" onClick={() => download(record)}>下载</Button> */}
            {/* </Tooltip> */}
          </>
        )
      },
    },

  ], [filter.sort, filter.sortType, authPoint, authPointMap])
  const [columnConfigs, columnProps, forceRequest] = useTableColumn(TableIdEnum.MEETING_CALL_RECORD, columns, [])
  const tableXWidth = useMemo(() => getTableXwidth(columnProps), [columnProps])
  const onTableChange: TableProps<MeetingCallRecordListItem>['onChange'] = (pagination, filters, sorter) => {
    const sort = getSortOrder<MeetingCallRecordFilter>(sorter)
    setFilter(sort)
  }
  // 过滤器权限控制
  const meetingCallRecordFilterItemsWithAuth = !authPointMap.menuTaskCallScoreExt1.result ? meetingCallRecordFilterItems.filter(item => !ScoreAdminFilterAvailable.includes(item.fieldName)) : meetingCallRecordFilterItems

  useEffect(() => {
    const { current: page, pageSize } = pagination
    const key = `${TableIdEnum.MEETING_CALL_RECORD}_FILTER`
    const val: FormValues = sStorage.get(key)
    sStorage.set(key, { ...val, page, pageSize: 1, _pageSize: pageSize })
  }, [pagination])

  /**
   * 获取权限点
   */
  const getAuthPoint = async () => {
    const [res, err] = await getUserAuthPoint([
      { object: 'tcc.menu.task.call.score', action: 'r' },
      { object: 'tcc.menu.task.call.score', action: 'u' },
      { object: 'tcc.menu.task.call.score', action: 'ex' },
      { object: 'tcc.menu.task.call.score', action: 'ext1' },
    ])
    const obj = {}
    if (err) {
      setAuthPoint({})
      return
    }
    const pointList = get(res, 'authPoints', [])
    pointList.forEach(item => {
      const key = `${item.object}-${item.action}`
      obj[key] = item.result
    })
    setAuthPoint(obj)
  }

  useEffect(() => {
    getAuthPoint()
  }, [])

  /**
   * 跳转评分页
   * @param recordId 通话记录ID
   * @param index 当前行索引
   */
  const handleToScoreDetail = async (record, index) => {
    const { recordId } = record
    const val: FormValues = sStorage.get(`${TableIdEnum.MEETING_CALL_RECORD}_FILTER`) || {}
    const page = (((val?.page ?? 1) - 1) * (val?._pageSize || 10)) + index + 1
    const createTime = moment().format('YYYY-MM-DD HH:mm:ss')
    sStorage.set(`${TableIdEnum.MEETING_CALL_RECORD}_FILTER`, { ...val, createTime, needTotal: 1 })
    history.push(`/task/score-detail?recordId=${recordId}&pageType=${PAGE_TYPE_ENUM.MEETING}`)
  }

  /**
   * 批量评分
   * 存储选中的记录到队列，跳转到详情页
   */
  const handleBatchScore = async () => {
    if (selectedRows.length === 0) {
      message.warning('请先选择要评分的通话记录')
      return
    }

    // 过滤出可评分的记录（有通话时长的）
    const scoreableRows = selectedRows.filter(item => item.duration)
    if (scoreableRows.length === 0) {
      message.warning('选中的记录均无法评分')
      return
    }

    const recordIds = scoreableRows.map(item => item.recordId)

    // 存储到队列
    const queueId = await storeQueue<MeetingScoreQueueData>(MEETING_SCORE_CATEGORY, {
      recordIds,
      pageType: PAGE_TYPE_ENUM.MEETING,
    })

    // 跳转到第一条记录的详情页
    history.push(`/task/score-detail?recordId=${recordIds[0]}&queue=${queueId}&pageType=${PAGE_TYPE_ENUM.MEETING}`)
  }

  // 将缓存的筛选数据转换为筛选组件需要的数据
  useEffect(() => {
    const val: FormValues = sStorage.get(`${TableIdEnum.MEETING_CALL_RECORD}_FILTER_VALUES`)
    if (filterRef?.current && val) {
      const vals = cloneDeep(val)
      // 通话时长
      if (val.durationFrom || val.durationTo) {
        vals.duration = [val.durationFrom || undefined, val.durationTo || undefined]
      }
      // 记录生成时间
      if (val.createTimeFrom || val.createTimeTo) {
        const start = val.createTimeFrom ? moment(val.createTimeFrom) : undefined
        const end = val.createTimeTo ? moment(val.createTimeTo) : undefined
        vals.createTime = [start, end]
      }
      // 客服关键词
      if (val.salesKeywordList) {
        val.salesKeywordList = handleKeyWordParams(val.salesKeywordList)
      }
      // 客户关键词
      if (val.customerKeywordList) {
        val.customerKeywordList = handleKeyWordParams(val.customerKeywordList)
      }
      filterRef.current.setValues(vals)
      setFilter(val)
    }
  }, [filterRef])

  /** 搜索 */
  const handleSearch = (filter) => {
    const val = { ...filter, page: 1 }
    const newVal = Object.fromEntries(
      Object.entries({ ...val }).filter((item: [string, any]) => {
        const [, value] = item
        if (value === null || value === undefined) return false
        if (typeof value === 'object') {
          return Object.keys(value).length !== 0
        }
        return value !== ''
      }),
    )
    sStorage.set(`${TableIdEnum.MEETING_CALL_RECORD}_FILTER`, newVal)
    const salesKeywordList = handleKeyWordParams(val.salesKeywordList)
    const customerKeywordList = handleKeyWordParams(val.customerKeywordList)
    setFilter({ ...val, salesKeywordList, customerKeywordList })
    reload()
  }

  return (
    <>
      <Card className="mt20 mb20">
        <CommonFilters
          cRef={filterRef}
          className="custom-Label"
          itemList={meetingCallRecordFilterItemsWithAuth}
          onSubmit={handleSearch}
          onReset={() => {
            sStorage.remove(`${TableIdEnum.MEETING_CALL_RECORD}_FILTER`)
            resetFilter(initFilter)
          }}
          tableId={TableIdEnum.MEETING_CALL_RECORD}
          standstill={9}
        />
      </Card>
      <Card>
        <ConfigTableHeader columnConfigs={columnConfigs} tableId={TableIdEnum.MEETING_CALL_RECORD} onSuccess={forceRequest} />
        <TableDownload className="ml20" exportMethod={exportMeetingCallRecordList} filter={filter} />
        {authPoint?.[menuTaskCallScoreEx] && <TableDownload className="ml20" exportMethod={exportMeetingCallRecordWithQCResultList} filter={filter} btnText="下载质检评分列表" />}
        {authPoint?.[menuTaskCallScoreU] && (
          <Button
            className="ml20"
            type="primary"
            disabled={selectedRows.length === 0}
            onClick={handleBatchScore}
          >
            批量评分{selectedRows.length > 0 ? `（${selectedRows.length}）` : ''}
          </Button>
        )}
        <Table
          style={{ maxWidth: tableXWidth + 'px' }}
          tableLayout="fixed"
          className="mt20"
          loading={loading}
          pagination={pagination}
          scroll={{ x: tableXWidth }}
          rowKey={rowKey}
          dataSource={meetingCallRecordList}
          columns={columnProps}
          onChange={onTableChange}
          rowSelection={authPoint?.[menuTaskCallScoreU] ? {
            ...rowSelection,
            getCheckboxProps: (record: MeetingCallRecordListItem) => ({
              disabled: !record.duration,
            }),
          } : undefined}
        />
      </Card>
    </>
  )
}
